<script lang="ts">
	import { Button, Container, Select, Stack, Text, Toggle, toast } from '@Shelly/shelly-design-system';
	import { onMount } from 'svelte';

	import { real_id } from 'src/helpers/interface_helpers';
	import { devices } from 'src/stores/lists/devices';
	import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
	import { deviceRuntimeResource } from 'src/stores/device-runtime/resource-state';
	import {
		clearDeviceRuntimeSelectorCaches,
		getDevice,
		getDeviceSettings,
		getDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { loadingData } from 'src/stores/ui/loading-data';
	import { deviceRuntimeDiagnostics } from 'src/stores/features/dev/device-runtime-diagnostics';

	export let onRefresh: () => void = () => {};

	let selectedDeviceId = '';
	let selectedDeviceOption: { title: string; value: string } | undefined;
	let autoLog = false;
	let lastLoggedEventId = 0;

	onMount(() => {
		deviceRuntimeDiagnostics.setVerboseCapture(true);
		return () => deviceRuntimeDiagnostics.setVerboseCapture(false);
	});

	$: deviceIds = Object.keys($devices);
	$: deviceOptions = deviceIds
		.map((id) => {
			const device = $devices[id];
			const label = device?.name || device?.type || 'Unnamed device';
			return {
				title: `${label} — ${id}`,
				value: id,
			};
		})
		.sort((a, b) => a.title.localeCompare(b.title));
	$: physicalDeviceIds = [...new Set(deviceIds.map((id) => real_id(id)))];
	$: statusIds = Object.keys($devices_status);
	$: settingsIds = Object.keys($devices_settings);
	$: physicalDeviceIdSet = new Set(physicalDeviceIds);
	$: missingStatus = physicalDeviceIds.filter((id) => !$devices_status[id]);
	$: missingSettings = physicalDeviceIds.filter((id) => !$devices_settings[id]);
	$: orphanStatus = statusIds.filter((id) => !physicalDeviceIdSet.has(id));
	$: orphanSettings = settingsIds.filter((id) => !physicalDeviceIdSet.has(id));
	$: if (!selectedDeviceId && physicalDeviceIds.length > 0) {
		selectedDeviceId = deviceOptions[0]?.value ?? '';
		selectedDeviceOption = deviceOptions[0];
	}
	$: normalizedSelectedId = real_id(selectedDeviceId);
	$: selectedDevice = $devices[selectedDeviceId] ?? $devices[normalizedSelectedId];
	$: selectedStatus = $devices_status[normalizedSelectedId];
	$: selectedSettings = $devices_settings[normalizedSelectedId];
	$: lastEvent = $deviceRuntimeDiagnostics.at(-1);
	$: warnings = [
		...(missingSettings.length
			? [`${missingSettings.length} physical devices are missing settings`]
			: []),
		...(missingStatus.length ? [`${missingStatus.length} physical devices are missing status`] : []),
		...(orphanSettings.length ? [`${orphanSettings.length} settings records are orphaned`] : []),
		...(orphanStatus.length ? [`${orphanStatus.length} status records are orphaned`] : []),
		...($deviceRuntimeResource.phase === 'ready' && settingsIds.length === 0
			? ['Runtime is ready but the settings map is empty']
			: []),
		...(!$loadingData && $deviceRuntimeResource.phase === 'loading'
			? ['Global loading is false while device runtime is loading']
			: []),
	];
	$: snapshot = {
		capturedAt: new Date().toISOString(),
		resource: {
			phase: $deviceRuntimeResource.phase,
			generation: $deviceRuntimeResource.generation,
			updatedAt: $deviceRuntimeResource.updatedAt,
			ageMs: $deviceRuntimeResource.updatedAt
				? Date.now() - $deviceRuntimeResource.updatedAt
				: undefined,
			errorType:
				$deviceRuntimeResource.error instanceof Error
					? $deviceRuntimeResource.error.name
					: $deviceRuntimeResource.error
						? typeof $deviceRuntimeResource.error
						: undefined,
		},
		globalLoading: $loadingData,
		counts: {
			devices: deviceIds.length,
			physicalDevices: physicalDeviceIds.length,
			statuses: statusIds.length,
			settings: settingsIds.length,
			events: $deviceRuntimeDiagnostics.length,
		},
		selectorCaches: {
			devices: getDevice.cacheSize(),
			statuses: getDeviceStatus.cacheSize(),
			settings: getDeviceSettings.cacheSize(),
		},
		invariants: {
			missingStatus,
			missingSettings,
			orphanStatus,
			orphanSettings,
			warnings,
		},
		selectedDevice: selectedDeviceId
			? {
					id: selectedDeviceId,
					normalizedId: normalizedSelectedId,
					present: Boolean(selectedDevice),
					statusPresent: Boolean(selectedStatus),
					settingsPresent: Boolean(selectedSettings),
					statusKeys: Object.keys(selectedStatus ?? {}),
					settingsKeys: Object.keys(selectedSettings ?? {}),
				}
			: undefined,
	};

	$: if (autoLog && lastEvent && lastEvent.id !== lastLoggedEventId) {
		lastLoggedEventId = lastEvent.id;
		console.log('[Device runtime event]', lastEvent);
	}

	const logSnapshot = () => {
		console.log('[Device runtime snapshot]', snapshot);
		console.log('[Device runtime recent events]', $deviceRuntimeDiagnostics.slice(-20));
	};

	const handleDeviceSelection = (event: CustomEvent) => {
		const selected = event.detail;
		selectedDeviceId =
			typeof selected === 'object' && selected !== null ? selected.value : (selected ?? '');
	};

	const logSelectedDevice = () => {
		console.log('[Device runtime selected device]', selectedDevice);
		console.log('[Device runtime selected status]', selectedStatus);
		console.log('[Device runtime selected settings]', selectedSettings);
	};

	const copySnapshot = async () => {
		try {
			await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
			toast.open({ type: 'success', title: 'Device runtime snapshot copied' });
		} catch {
			toast.open({ type: 'danger', title: 'Could not copy device runtime snapshot' });
		}
	};

	const clearCaches = () => {
		clearDeviceRuntimeSelectorCaches();
		toast.open({ type: 'success', title: 'Device runtime selector caches cleared' });
	};
</script>

<Stack gap="m" class="device-runtime-inspector">
	<Stack gap="xs">
		<Text styleType="body-small">
			Phase: <strong>{$deviceRuntimeResource.phase}</strong> · Generation:
			{$deviceRuntimeResource.generation} · Global loading: {$loadingData}
		</Text>
		<Text styleType="body-small">
			Devices: {deviceIds.length} · Physical: {physicalDeviceIds.length} · Status:
			{statusIds.length} · Settings: {settingsIds.length}
		</Text>
		<Text styleType="body-small">
			Selector caches — devices: {getDevice.cacheSize()}, status: {getDeviceStatus.cacheSize()},
			settings: {getDeviceSettings.cacheSize()}
		</Text>
		{#if $deviceRuntimeResource.updatedAt}
			<Text styleType="body-small">
				Last successful hydration:
				{Math.round((Date.now() - $deviceRuntimeResource.updatedAt) / 1000)}s ago
			</Text>
		{/if}
	</Stack>

	{#if warnings.length}
		<Container class="runtime-warnings" padding="s">
			<Text styleType="body-small" class="text-bold">Invariant warnings</Text>
			{#each warnings as warning}
				<Text styleType="body-small">• {warning}</Text>
			{/each}
		</Container>
	{:else}
		<Text styleType="body-small">No runtime invariant warnings.</Text>
	{/if}

	<Select
		id="device-runtime-inspector-id"
		aria-label="Inspect device"
		label="Inspect device ID"
		icon="fas fa-microchip"
		isAutocomplete
		options={deviceOptions}
		bind:value={selectedDeviceOption}
		on:change={handleDeviceSelection}
	/>

	{#if selectedDeviceId}
		<Container padding="s" class="runtime-device-summary">
			<Text styleType="body-small">Normalized ID: {normalizedSelectedId}</Text>
			<Text styleType="body-small">Device present: {Boolean(selectedDevice)}</Text>
			<Text styleType="body-small">Status present: {Boolean(selectedStatus)}</Text>
			<Text styleType="body-small">Settings present: {Boolean(selectedSettings)}</Text>
			<Text styleType="body-small">
				Status keys: {Object.keys(selectedStatus ?? {}).join(', ') || 'none'}
			</Text>
			<Text styleType="body-small">
				Settings keys: {Object.keys(selectedSettings ?? {}).join(', ') || 'none'}
			</Text>
			<Button size="s" variant="secondary" class="mt-s" on:click={logSelectedDevice}>
				Console.log selected device stores
			</Button>

			<Text styleType="body-small" class="mt-s">
				Store values may include device credentials. Use only in a trusted development session.
			</Text>
			<details>
				<summary>Device store value</summary>
				<pre>{JSON.stringify(selectedDevice, null, 2)}</pre>
			</details>
			<details>
				<summary>Status store value</summary>
				<pre>{JSON.stringify(selectedStatus, null, 2)}</pre>
			</details>
			<details>
				<summary>Settings store value</summary>
				<pre>{JSON.stringify(selectedSettings, null, 2)}</pre>
			</details>
		</Container>
	{/if}

	<Toggle
		id="device-runtime-console-tracking"
		size="m"
		checked={autoLog}
		on:click={() => (autoLog = !autoLog)}
	>
		Console-log new runtime events
	</Toggle>

	<Stack gap="s" class="d-flex flex-wrap">
		<Button size="s" on:click={onRefresh}>Refresh runtime data</Button>
		<Button size="s" variant="secondary" on:click={logSnapshot}>Console.log snapshot</Button>
		<Button size="s" variant="secondary" on:click={copySnapshot}>Copy redacted snapshot</Button>
		<Button size="s" variant="secondary" on:click={clearCaches}>Clear selector caches</Button>
		<Button size="s" variant="secondary" on:click={() => deviceRuntimeDiagnostics.clear()}>
			Clear event history
		</Button>
	</Stack>

	<Container padding="s" class="runtime-events">
		<Text styleType="body-small" class="text-bold">
			Recent events ({$deviceRuntimeDiagnostics.length}/200)
		</Text>
		{#if $deviceRuntimeDiagnostics.length}
			<pre>{JSON.stringify($deviceRuntimeDiagnostics.slice(-20), null, 2)}</pre>
		{:else}
			<Text styleType="body-small">No runtime events recorded.</Text>
		{/if}
	</Container>
</Stack>

<style>
	.runtime-warnings {
		background: var(--color-warning-50, #fff8e1);
		border-radius: 0.5rem;
	}

	.runtime-device-summary,
	.runtime-events {
		background: var(--color-neutral-50, #f6f7f8);
		border-radius: 0.5rem;
	}

	pre {
		margin: 0.5rem 0 0;
		max-height: 18rem;
		overflow: auto;
		white-space: pre-wrap;
		font-size: 0.75rem;
	}
</style>
